// Server function: establish a DO-issued guest identity for public/external apps. The browser calls
// this once on startup (start.ts). It reads the browser's stored guest token from the request
// context (x-vibe-guest-token) and, if the DO still recognizes it, returns it unchanged; otherwise
// it mints a fresh DO-owned token. The DO owns the guest id, so the browser can't forge it — this is
// what makes guest `createdBy` trustworthy. Members (private/internal) don't need it → { guestToken:
// null }. SERVER-ONLY body: '@vibe/db/server' is stripped from the client bundle by TanStack Start.
import { createServerFn } from '@tanstack/react-start';
import { mintGuestToken, resolveGuestToken } from '@vibe/db/server';
import { getEnv } from '../services/env.service';
import { getGuestToken } from '../services/request-context.service';

const PRIVATE_MODES = new Set(['private', 'internal']);

export const ensureVibeGuest = createServerFn({ method: 'POST' }).handler(
  async (): Promise<{ guestToken: string | null }> => {
    const mode = await getEnv('VIBE_APP_MODE');
    if (PRIVATE_MODES.has(mode ?? '')) return { guestToken: null };

    // DO not provisioned (App DO storage flag off, or the transient pre-bootstrap cold-start window):
    // there's no gateway to mint against, so there's no guest identity. Return cleanly instead of
    // letting the transport throw on a missing VIBE_DO_URL — that failure would just be swallowed by
    // start.ts, but not before a wasted round-trip + a misleading error log on every public-app boot.
    if (!(await getEnv('VIBE_DO_URL'))) return { guestToken: null };

    const existing = getGuestToken();
    if (existing && (await resolveGuestToken(existing))) {
      return { guestToken: existing };
    }
    const minted = await mintGuestToken();
    return { guestToken: minted.guestToken };
  },
);
